import { extensionFor } from '../capture/mimeTypes';
import { noteError, noteEvent, patchDiagnostics } from '../lib/diagnostics';
import { idbAll, idbAvailable, idbDelete, idbGet, idbPut } from './idb';
import { getSupabase, storagePathFor, uploadClip, writeSessionRow } from './supabaseClient';
import type { QueueRecord } from './types';

/**
 * Kiosk delivery queue.
 *
 * Durable by design: the blob, the phone number, and the attempt count live in
 * IndexedDB, so a reload, a crash, or the venue wifi dying mid-event costs us
 * nothing. The guest is told "on its way" the moment their record is on disk;
 * this queue is what makes it true.
 *
 * Two steps per record, and then the kiosk is done:
 *   1. upload the blob to Supabase Storage    → status 'uploaded'
 *   2. write the session row with the phone   → record deleted
 *
 * That is the whole job. Delivery happens server-side from there — a database
 * webhook hands the clip to the transcode worker, and a trigger fires the
 * notify-sms function once an MP4 exists. The kiosk does not need to stay online,
 * or even powered on, for the text to go out. Twilio is never touched from here.
 *
 * See docs/DELIVERY.md.
 */

const MAX_ATTEMPTS = 8;
const BASE_BACKOFF_MS = 4_000;
const MAX_BACKOFF_MS = 5 * 60_000;
const TICK_MS = 5_000;

let timer: number | null = null;
let draining = false;
let started = false;
let confirmedUploads = 0;

function backoffFor(attempts: number): number {
  const ms = BASE_BACKOFF_MS * Math.pow(2, Math.max(0, attempts - 1));
  return Math.min(ms, MAX_BACKOFF_MS);
}

export async function enqueueClip(params: {
  sessionId: string;
  blob: Blob;
  mimeType: string;
  /** Empty when the guest declined to give a number and took a code instead. */
  phone: string;
  smsConsent: boolean;
  shareCode?: string | null;
}): Promise<boolean> {
  const now = Date.now();
  const record: QueueRecord = {
    id: params.sessionId,
    blob: params.blob,
    mimeType: params.mimeType,
    phone: params.phone,
    smsConsent: params.smsConsent,
    shareCode: params.shareCode ?? null,
    status: 'pending',
    attempts: 0,
    createdAt: now,
    updatedAt: now,
    nextAttemptAt: now,
    lastError: null,
    storagePath: null,
  };

  try {
    await idbPut(record);
    noteEvent(`queued clip ${params.sessionId}`);
    await refreshDepth();
    void drain();
    return true;
  } catch (err) {
    // Nothing left to fall back to — the blob only existed in memory. Surface it
    // in diagnostics; the guest has already been told it is on its way.
    noteError('queue.enqueue', err);
    return false;
  }
}

/** Starts the background worker. Idempotent. */
export async function startQueue(): Promise<void> {
  if (started) return;
  started = true;

  if (!(await idbAvailable())) {
    noteError('queue.start', new Error('IndexedDB unavailable — kiosk queue disabled'));
    return;
  }

  // Anything left mid-flight by a crash or reload goes back to pending.
  try {
    const all = await idbAll();
    for (const record of all) {
      if (record.status === 'uploading') {
        await idbPut({ ...record, status: 'pending', updatedAt: Date.now() });
      }
    }
    if (all.length > 0) noteEvent(`queue resumed with ${all.length} record(s)`);
  } catch (err) {
    noteError('queue.resume', err);
  }

  window.addEventListener('online', () => {
    noteEvent('network back online');
    void drain();
  });

  timer = window.setInterval(() => void drain(), TICK_MS);
  await refreshDepth();
  void drain();
}

export function stopQueue(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  started = false;
}

export async function refreshDepth(): Promise<void> {
  try {
    const all = await idbAll();
    patchDiagnostics({
      queueDepth: all.filter((r) => r.status !== 'failed').length,
      smsQueued: all.filter((r) => r.status === 'uploaded').length,
    });
  } catch (err) {
    noteError('queue.depth', err);
  }
}

/** Full queue contents, for the admin view. */
export async function queueSnapshot(): Promise<Omit<QueueRecord, 'blob'>[]> {
  try {
    const all = await idbAll();
    return all
      .map(({ blob: _blob, ...rest }) => rest)
      .sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

async function drain(): Promise<void> {
  if (draining) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

  draining = true;
  patchDiagnostics({ queueSending: true });
  try {
    const all = await idbAll();
    const due = all
      .filter((r) => r.status !== 'failed' && r.nextAttemptAt <= Date.now())
      .sort((a, b) => a.createdAt - b.createdAt);

    // One at a time: the board is the performance floor and a guest may be
    // mid-session on it.
    for (const record of due) {
      await processRecord(record);
    }
  } catch (err) {
    noteError('queue.drain', err);
  } finally {
    draining = false;
    patchDiagnostics({ queueSending: false });
    await refreshDepth();
  }
}

async function processRecord(record: QueueRecord): Promise<void> {
  const supabase = await getSupabase();
  if (!supabase) {
    // Unconfigured dev machine. Leave the record alone rather than burning
    // attempts against a backend that does not exist.
    return;
  }

  try {
    if (record.status === 'pending' || record.status === 'uploading') {
      await markAttempt(record, 'uploading');
      if (!record.blob) throw new Error('Queue record has no blob');

      const path = storagePathFor(record.id, extensionFor(record.mimeType), 'kiosk');
      const { error } = await uploadClip(supabase, path, record.blob, record.mimeType);
      if (error) throw error;

      record = {
        ...record,
        status: 'uploaded',
        storagePath: path,
        lastError: null,
        attempts: 0,
        nextAttemptAt: Date.now(),
        updatedAt: Date.now(),
      };
      // Drop the blob now that storage holds it — hundreds of these sit on the
      // kiosk's disk otherwise.
      await idbPut({ ...record, blob: null });
      confirmedUploads += 1;
      patchDiagnostics({ uploadsConfirmed: confirmedUploads });
      noteEvent(`uploaded ${record.id}`);
    }

    if (record.status === 'uploaded') {
      // status='uploaded' is the server's cue to pick the clip up. Writing this
      // row is the last thing the kiosk does for this guest.
      const { error } = await writeSessionRow(supabase, {
        session_id: record.id,
        mode: 'kiosk' as const,
        status: 'uploaded' as const,
        clip_path: record.storagePath,
        clip_mime: record.mimeType,
        phone_number: record.smsConsent && record.phone ? record.phone : null,
        sms_consent: record.smsConsent,
        share_code: record.shareCode,
      });
      if (error) throw error;

      await idbDelete(record.id);
      noteEvent(`${record.id} handed off to delivery`);
    }
  } catch (err) {
    await handleFailure(record, err);
  }
}

async function markAttempt(record: QueueRecord, status: QueueRecord['status']): Promise<void> {
  await idbPut({
    ...record,
    status,
    attempts: record.attempts + 1,
    updatedAt: Date.now(),
  });
}

async function handleFailure(record: QueueRecord, err: unknown): Promise<void> {
  const message = err instanceof Error ? err.message : String(err);
  const current = (await idbGet(record.id)) ?? record;
  const attempts = current.attempts;
  const exhausted = attempts >= MAX_ATTEMPTS;

  noteError('queue.send', `${record.id} attempt ${attempts}: ${message}`);

  await idbPut({
    ...current,
    // Back to a retryable state; 'uploaded' records keep their storage path.
    status: exhausted ? 'failed' : current.status === 'uploaded' ? 'uploaded' : 'pending',
    lastError: message,
    nextAttemptAt: Date.now() + backoffFor(attempts),
    updatedAt: Date.now(),
  });
}

/** Admin action: force an immediate drain attempt. */
export async function retryNow(): Promise<void> {
  try {
    const all = await idbAll();
    for (const record of all) {
      await idbPut({
        ...record,
        status: record.status === 'failed' ? 'pending' : record.status,
        nextAttemptAt: Date.now(),
        attempts: record.status === 'failed' ? 0 : record.attempts,
        updatedAt: Date.now(),
      });
    }
  } catch (err) {
    noteError('queue.retryNow', err);
  }
  await drain();
}
